/**
 * Nexus Academy — module + step content.
 *
 * Each module has: id, title, tag ("Growth" | "Business" | "Craft"), summary, hero/tone,
 * duration (minutes to read), icon key, and an array of `steps`.
 *
 * Each step has:
 *   - id (stable per module — used for localStorage progress)
 *   - title (short imperative)
 *   - body: rich content described as an array of blocks
 *       { type: "p", text }
 *       { type: "h", text }
 *       { type: "list", items: string[] }
 *       { type: "quote", text, cite? }
 *       { type: "tip", label, text }
 *       { type: "warn", label, text }
 *       { type: "action", label, cta, href } — links to another Nexus surface
 *
 * When video content is added later, extend a step with { videoUrl } and the
 * reader will render an inline player above the body.
 */

export const MODULES = [
  // ---------------------------------------------------------------- GROWTH 101
  {
    id: "m1",
    title: "Growth 101 — from 0 to 10k followers",
    tag: "Growth",
    tone: "text-nexus-pink",
    accent: "from-nexus-pink to-fuchsia-500",
    duration: 12,
    icon: "TrendingUp",
    summary: "The exact playbook creators use to go from a bare profile to their first 10,000 followers — no ads, no hacks, no purchased engagement.",
    steps: [
      {
        id: "s1",
        title: "Pick one niche you can post about for a year",
        body: [
          { type: "p", text: "The single biggest reason accounts stall at ~500 followers is that they post about six things: fashion on Monday, gym on Wednesday, food on Friday, motivation on Sunday. To the algorithm, that account is unreadable — it can't figure out who to show your content to." },
          { type: "h", text: "The 3-question test" },
          { type: "list", items: [
            "Can I make 100 posts about this without running out of ideas?",
            "Is there a specific person (not everyone) who would follow this account?",
            "Would I still care about this topic if it made me zero money?",
          ]},
          { type: "p", text: "If you can answer yes to all three, that's your niche. If you can't yet, don't post until you can — spend a week on the next step first." },
          { type: "tip", label: "Insider tip", text: "\"Fitness\" is not a niche. \"Home workouts for busy dads\" is a niche. The narrower you go, the faster you grow." },
        ],
      },
      {
        id: "s2",
        title: "Study your top 10 competitors for one hour",
        body: [
          { type: "p", text: "Before you post a single thing, spend 60 minutes reverse-engineering the accounts that are already winning in your niche. Not to copy them — to see the pattern they don't advertise." },
          { type: "h", text: "What to write down" },
          { type: "list", items: [
            "Their top 3 posts of all time — what's the hook in the first 2 seconds?",
            "The format they repeat (talking-head? voiceover? text on screen?)",
            "How often they post (3x/week? daily? weekends only?)",
            "The exact words in their bio and pinned post",
          ]},
          { type: "quote", text: "The algorithm rewards patterns. Steal the pattern, keep your point of view.", cite: "— every $10k/mo creator on Nexus" },
        ],
      },
      {
        id: "s3",
        title: "Build a content bank of 20 hooks",
        body: [
          { type: "p", text: "You will run out of ideas by post 15 unless you build a bank now. Write 20 hooks before you write a single script." },
          { type: "h", text: "Hooks that work on any niche" },
          { type: "list", items: [
            "\"3 things I wish I knew before I started ___\"",
            "\"Nobody talks about this but ___\"",
            "\"I tried ___ for 30 days and here's what happened\"",
            "\"The biggest mistake beginners make in ___\"",
            "\"POV: you just realised ___\"",
            "\"Watch this before you ___\"",
          ]},
          { type: "warn", label: "Don't skip this", text: "Empty hook bank = burnout by week 3. Every top creator maintains a rolling list of 50+ hooks." },
        ],
      },
      {
        id: "s4",
        title: "Nail your first 3 seconds",
        body: [
          { type: "p", text: "IG and TikTok decide within the first 3 seconds whether to keep showing your video. 87% of views are lost at second 3." },
          { type: "h", text: "The 3-second rule" },
          { type: "list", items: [
            "Start mid-action — never with \"hi guys\"",
            "First frame must have visual movement OR text",
            "First words must promise a specific payoff",
            "No logo intros, no fade-ins, no captions loading",
          ]},
          { type: "action", label: "Try the Nexus editor", cta: "Open Studio", href: "/creator-studio" },
        ],
      },
      {
        id: "s5",
        title: "Post at the same time, 4x per week, for 6 weeks",
        body: [
          { type: "p", text: "The algorithm rewards consistency more than quality. A 6/10 post four times a week beats a 10/10 post once every three weeks — every single time." },
          { type: "h", text: "The 6-week discipline" },
          { type: "list", items: [
            "Pick 4 days you can commit to (e.g. Mon/Wed/Fri/Sun)",
            "Same time each day — track your audience's active hours in Insights",
            "Never skip. If you can't film, repurpose an old post as a Reel",
            "Do not check analytics for the first 21 days — it messes with your head",
          ]},
          { type: "tip", label: "Real talk", text: "Week 1-3 will feel like nobody's watching. Week 4-6 the compounding kicks in. Stay in the game." },
        ],
      },
      {
        id: "s6",
        title: "Reply to every comment for the first 90 days",
        body: [
          { type: "p", text: "Comments are the fastest signal to the algorithm that a post is a hit. Every reply you write pushes the post to more feeds." },
          { type: "list", items: [
            "Reply within the first hour of posting — that's when the algorithm is measuring",
            "Longer replies (10+ words) count more than emoji-only",
            "Ask a question in your reply to trigger a second comment",
            "Pin your best comment thread to bookmark it for future viewers",
          ]},
        ],
      },
      {
        id: "s7",
        title: "Turn 1k followers into your first paid deal",
        body: [
          { type: "p", text: "You don't need 100k to make money. Nexus creators land their first paid collab as early as 800 real followers because brands care about niche match, not raw numbers." },
          { type: "list", items: [
            "Fill out every field of your Nexus profile — especially rate + niche",
            "Add 3 examples of your best work to your portfolio",
            "Set your rate honestly — under-pricing signals amateur",
            "Apply to 5 campaigns per week, no exceptions",
          ]},
          { type: "action", label: "Complete your profile", cta: "Edit profile", href: "/creator/profile" },
        ],
      },
    ],
  },

  // ------------------------------------------------------------------- PRICING
  {
    id: "m2",
    title: "How to price your first paid collab",
    tag: "Business",
    tone: "text-nexus-blue",
    accent: "from-nexus-blue to-cyan-400",
    duration: 8,
    icon: "BookOpen",
    summary: "The pricing frameworks working creators actually use — including the exact formula that stops you leaving money on the table.",
    steps: [
      {
        id: "s1",
        title: "Understand what brands are actually buying",
        body: [
          { type: "p", text: "Brands are not paying for your video. They're paying for access to a specific audience that trusts you. The more specific + trusting your audience, the more your post is worth." },
          { type: "h", text: "What actually drives your rate" },
          { type: "list", items: [
            "Audience niche match (a 5k gaming account beats 50k general lifestyle)",
            "Engagement rate — not follower count",
            "Content quality signal (edited > raw)",
            "Deliverable type (a Reel > a Story > a static post)",
            "Usage rights (can they run it as an ad? that's 3-5x)",
          ]},
        ],
      },
      {
        id: "s2",
        title: "The CPM formula (the honest one)",
        body: [
          { type: "p", text: "The industry-standard baseline is $10-20 CPM (per 1,000 average views). For Reels/TikTok short-form:" },
          { type: "quote", text: "Rate = (Avg views ÷ 1000) × CPM × Deliverable multiplier", cite: "The Nexus Rate Card, v2026" },
          { type: "h", text: "Deliverable multipliers" },
          { type: "list", items: [
            "Story (24h): 0.5×",
            "Static post (feed): 1×",
            "Reel / TikTok: 1.5×",
            "Long-form YouTube video: 3-5×",
            "Add usage rights (paid ads): +50-200%",
            "Add exclusivity (won't post competitors for 30 days): +25%",
          ]},
        ],
      },
      {
        id: "s3",
        title: "Do the math on your account",
        body: [
          { type: "p", text: "Grab your Instagram Insights. Take the average views of your last 10 Reels — that's your negotiating baseline." },
          { type: "h", text: "Example: 8k average views, one Reel with usage rights" },
          { type: "list", items: [
            "Base: (8000 ÷ 1000) × $15 = $120",
            "Reel multiplier: $120 × 1.5 = $180",
            "Usage rights (30 days paid ads): $180 × 1.75 = $315",
            "Round confidently: quote $325",
          ]},
          { type: "tip", label: "Never undersell", text: "It's easier to negotiate DOWN than up. Start 15-20% above your target." },
        ],
      },
      {
        id: "s4",
        title: "The 3 questions you must ask BEFORE quoting",
        body: [
          { type: "p", text: "Never send a rate before you know these three things:" },
          { type: "list", items: [
            "\"What deliverables specifically — how many posts, which platforms, video or static?\"",
            "\"How long do you need usage rights, and does that include paid amplification?\"",
            "\"Is there any exclusivity requirement in the same category?\"",
          ]},
          { type: "warn", label: "Don't skip this", text: "90% of underpaid deals happen because the creator quoted before knowing these three answers." },
        ],
      },
      {
        id: "s5",
        title: "What to say when they push back",
        body: [
          { type: "p", text: "\"That's outside our budget\" is not a rejection — it's the start of the negotiation. Two moves:" },
          { type: "h", text: "Move 1: Trade scope, not price" },
          { type: "p", text: "\"Totally understand. My rate for a Reel with usage is $325 — for a Reel WITHOUT usage rights, I can do $215.\" Never drop the rate, drop the scope." },
          { type: "h", text: "Move 2: Add value" },
          { type: "p", text: "\"Happy to hold that rate if we can extend the campaign to 3 posts instead of 1 — that brings your CPM down.\" You get more work, they get better economics." },
        ],
      },
      {
        id: "s6",
        title: "Use Nexus to skip the awkward conversation",
        body: [
          { type: "p", text: "The whole negotiation happens inside Nexus. Brands post rates upfront. You accept, counter, or walk. No email chains, no ghosting, no invoicing headaches." },
          { type: "list", items: [
            "Every deal is escrowed — you get paid on delivery, guaranteed",
            "Counter-offers are one-click",
            "Your rate history builds a track record that unlocks bigger deals",
          ]},
          { type: "action", label: "Browse open campaigns", cta: "See campaigns", href: "/campaigns" },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------- HOOKS
  {
    id: "m3",
    title: "The hook formula that wins Reels",
    tag: "Craft",
    tone: "text-nexus-purple",
    accent: "from-nexus-purple to-fuchsia-500",
    duration: 6,
    icon: "PlayCircle",
    summary: "The 4-part hook structure top creators use to keep 90%+ of viewers past the first 3 seconds.",
    steps: [
      {
        id: "s1",
        title: "Why hooks matter more than everything else",
        body: [
          { type: "p", text: "Meta's own data (leaked 2024): 87% of Reels lose the viewer between second 0 and second 3. That means the first 3 seconds decide whether the other 27 seconds even exist." },
          { type: "p", text: "You could spend 4 hours editing and lose the video in the opening 3 seconds. Which is why the top 1% of creators spend more time writing hooks than filming." },
        ],
      },
      {
        id: "s2",
        title: "The 4-part hook (steal this)",
        body: [
          { type: "h", text: "Every winning hook has these 4 elements in the first 3 seconds" },
          { type: "list", items: [
            "1. Pattern interrupt — visual, sound, or motion that stops the scroll",
            "2. Specific promise — what will they learn / see / feel",
            "3. Loop tension — a question that only the payoff answers",
            "4. Character on screen (yes, even for voiceovers — cut to someone's face)",
          ]},
          { type: "quote", text: "Every second you delay the payoff, you lose 10% of the audience.", cite: "MrBeast, on his content strategy" },
        ],
      },
      {
        id: "s3",
        title: "10 hook templates that always work",
        body: [
          { type: "list", items: [
            "\"You've been doing X wrong your whole life\"",
            "\"3 signs you're about to ___\"",
            "\"I gave up ___ for 30 days — here's what happened\"",
            "\"POV: ___ (visual setup pays off in the last second)\"",
            "\"Nobody talks about this, but ___\"",
            "\"Watch this before you ___\"",
            "\"I'll never do ___ again after seeing this\"",
            "\"The reason ___ isn't working is ___\"",
            "\"If you've ever ___, this is for you\"",
            "\"Stop scrolling if ___\" (yes, saying \"stop scrolling\" works)",
          ]},
        ],
      },
      {
        id: "s4",
        title: "Hooks that KILL your reach (avoid these)",
        body: [
          { type: "warn", label: "Never open with these", text: "\"Hey guys, welcome back to my channel\" — audience gone. \"So today I want to talk about\" — audience gone. Any intro. Any logo. Any personal name-drop." },
          { type: "list", items: [
            "❌ Slow reveals / fade-ins",
            "❌ \"Hi everyone!\"",
            "❌ Music before dialogue",
            "❌ Wide establishing shots",
            "❌ Anything that could equally start any other video",
          ]},
        ],
      },
      {
        id: "s5",
        title: "The 'test 3 hooks per idea' rule",
        body: [
          { type: "p", text: "Write your video ONCE, then write 3 different hooks for it. Post the video 3 times to 3 different accounts (or 1 account 3 weeks apart) and see which hook wins." },
          { type: "p", text: "The same video with a better hook can 10x its views. Testing hooks is the highest-leverage thing you can do." },
          { type: "action", label: "Use auto-clips to test hooks fast", cta: "Try Auto Clips", href: "/creator-studio?tab=auto-clips" },
        ],
      },
      {
        id: "s6",
        title: "The 3-line caption formula that stops the scroll",
        body: [
          { type: "p", text: "The caption is the SECOND hook. When the video starts, viewers glance at the first line. It has to earn the next glance." },
          { type: "h", text: "The 3-line formula" },
          { type: "list", items: [
            "Line 1: The most emotional / provocative single sentence",
            "Line 2: The turn — a contradiction or question",
            "Line 3: A soft CTA or open loop",
          ]},
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ CONTRACTS
  {
    id: "m4",
    title: "Contract red flags every creator misses",
    tag: "Business",
    tone: "text-nexus-blue",
    accent: "from-nexus-blue to-teal-400",
    duration: 10,
    icon: "BookOpen",
    summary: "The 8 clauses buried in brand deal contracts that eat your rights, your rates, and your reputation — plus what to say instead.",
    steps: [
      {
        id: "s1",
        title: "\"Perpetual, worldwide, royalty-free\" — the death clause",
        body: [
          { type: "warn", label: "Red flag", text: "If any usage clause contains all three words — perpetual, worldwide, royalty-free — walk unless the deal is at least 5x your normal rate." },
          { type: "p", text: "This clause means the brand can use your face and voice forever, in any country, without paying you again. Ever. That's a lifetime licence for the price of one Reel." },
          { type: "h", text: "What to say instead" },
          { type: "quote", text: "\"I license usage rights for 90 days, in the campaign region, for organic use only. Paid amplification and extensions renew at 40% of the original fee.\"" },
        ],
      },
      {
        id: "s2",
        title: "Exclusivity that's too broad",
        body: [
          { type: "p", text: "Every brand wants exclusivity so their competitors can't hire you. Fine — but reasonable exclusivity is 30 days in the same product category. Anything more is unpaid time." },
          { type: "list", items: [
            "❌ \"Creator will not work with any competitor\" (no time limit = forever)",
            "❌ \"...in the beauty industry\" (way too broad — mascara ≠ face cream)",
            "✅ \"...for 30 days from posting, in the same subcategory (mascara only)\"",
          ]},
        ],
      },
      {
        id: "s3",
        title: "\"Approval\" clauses that stall your money",
        body: [
          { type: "p", text: "Some contracts say the brand must \"approve\" your post before payment. Without a timeline, this is a trap — they can drag approval indefinitely." },
          { type: "h", text: "The fix" },
          { type: "quote", text: "\"Brand has 3 business days to review each deliverable. If no feedback is received within 3 business days, the deliverable is deemed approved.\"" },
          { type: "tip", label: "Nexus does this for you", text: "Every Nexus deal has a 3-day auto-approval window built in. If the brand doesn't respond, the money releases automatically." },
        ],
      },
      {
        id: "s4",
        title: "Content ownership vs. usage licence",
        body: [
          { type: "p", text: "There is a huge difference between \"the brand owns the content\" and \"the brand has a licence to use the content\". You want the second." },
          { type: "h", text: "Watch for these words" },
          { type: "list", items: [
            "❌ \"Creator assigns ownership of the content to Brand\" (you lose ALL rights)",
            "❌ \"Work-for-hire\" (US legal term — you lose ownership)",
            "✅ \"Creator retains ownership; grants Brand a licence to use the content for the agreed term\"",
          ]},
        ],
      },
      {
        id: "s5",
        title: "The morality clause you shouldn't sign blindly",
        body: [
          { type: "p", text: "Brands increasingly add \"morality clauses\" — if you get cancelled, they can terminate the deal AND claw back payment. Push back on the clawback." },
          { type: "list", items: [
            "✅ Accept termination if there's a genuine PR crisis",
            "❌ Reject clawback of already-paid fees for delivered work",
            "✅ Define what triggers it — no vague \"conduct unbecoming\"",
          ]},
        ],
      },
      {
        id: "s6",
        title: "Payment terms that will bury you",
        body: [
          { type: "warn", label: "Watch for", text: "\"Net 60\", \"Net 90\", or \"upon publication of final approval\" — these all mean you don't get paid for 2-3 months." },
          { type: "h", text: "What to negotiate" },
          { type: "list", items: [
            "50% upfront, 50% on delivery — the industry standard",
            "Never accept anything longer than Net 30",
            "Ask for late payment interest (5% per week overdue)",
          ]},
          { type: "tip", label: "Escrow > invoicing", text: "Nexus escrows the full fee before you start work. Delivery = instant release. No chasing." },
        ],
      },
      {
        id: "s7",
        title: "Whitelisting and dark-post usage",
        body: [
          { type: "p", text: "\"Whitelisting\" = the brand runs paid ads FROM your account. \"Dark posts\" = ads that use your content but don't appear on your feed. Both are worth 2-3x your base rate." },
          { type: "list", items: [
            "Always ask specifically: \"Will this include whitelisting or dark posts?\"",
            "If yes, add +100-200% to your fee",
            "Set a cap: \"Whitelisting for 30 days, max $10k in ad spend\"",
          ]},
        ],
      },
      {
        id: "s8",
        title: "The \"you must promote positively\" clause",
        body: [
          { type: "p", text: "Some brands add clauses requiring you to only speak positively about their product forever. This is legally messy AND kills your authenticity." },
          { type: "quote", text: "\"Creator will provide honest opinions based on genuine experience with the product. Creator will not make disparaging statements during the campaign term.\"" },
          { type: "p", text: "Note: \"during the campaign term\" and \"disparaging\" (not just \"negative\") are the magic words." },
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ BATCHING
  {
    id: "m5",
    title: "Editing sprints — batch a week in 90 min",
    tag: "Craft",
    tone: "text-nexus-purple",
    accent: "from-nexus-purple to-nexus-pink",
    duration: 9,
    icon: "PlayCircle",
    summary: "The batching system top creators use to make a full week of content in a single 90-minute editing sprint.",
    steps: [
      {
        id: "s1",
        title: "The switching-cost problem",
        body: [
          { type: "p", text: "Every time you switch between filming, editing, writing captions, and scheduling, your brain loses ~15 minutes of momentum. Do 4 posts one at a time = you lose an hour just to context-switching." },
          { type: "p", text: "The fix is batching — do ONE kind of task for a block of time, then switch. It sounds simple. It changes everything." },
        ],
      },
      {
        id: "s2",
        title: "The 4-stage sprint",
        body: [
          { type: "h", text: "Each stage is one uninterrupted block" },
          { type: "list", items: [
            "Stage 1 (20 min) — WRITE. Draft all 4 scripts in one sitting. No editing, no filming.",
            "Stage 2 (30 min) — FILM. Same outfit, same location, back-to-back takes. Don't stop to review.",
            "Stage 3 (30 min) — EDIT. Cut all 4 in a row using the same template.",
            "Stage 4 (10 min) — SCHEDULE. Write captions + hashtags + publish times, all at once.",
          ]},
          { type: "tip", label: "Total", text: "90 minutes = 4 posts = a full week of content." },
        ],
      },
      {
        id: "s3",
        title: "Set up your editor ONCE",
        body: [
          { type: "p", text: "The biggest time-drain in editing is fiddling with fonts, colours, and text placement every single post. Save a template with your fonts, brand kit, and text positions locked in." },
          { type: "list", items: [
            "One aspect ratio for the whole batch (9:16 for Reels/TikTok)",
            "One caption font — never change mid-week",
            "One filter or LUT — brands recognise your look",
            "One outro card — animated logo or handle",
          ]},
          { type: "action", label: "Save your brand kit in Studio", cta: "Set up Brand Kit", href: "/creator-studio?tab=brand-kits" },
        ],
      },
      {
        id: "s4",
        title: "The '3-look' filming trick",
        body: [
          { type: "p", text: "Change outfits ONCE mid-sprint. Wear look A for 2 videos, then look B for 2 videos. Post them across the week and it looks like 4 separate filming days." },
          { type: "list", items: [
            "Take 1 outfit change = perceived as 4 filming days",
            "Change background angle (not location) between takes",
            "Vary energy — take 1 is calm, take 2 is hyped, etc.",
          ]},
        ],
      },
      {
        id: "s5",
        title: "Reuse the same script skeleton",
        body: [
          { type: "p", text: "Every top creator has a script template they never break. Steal one that works:" },
          { type: "quote", text: "Hook (3s) → Payoff promise (2s) → 3 points, 5s each → Callback + CTA (5s)", cite: "Total: ~25 seconds — the algo-perfect length" },
          { type: "list", items: [
            "Fill in the 3 points and the hook — everything else stays identical",
            "Once you have the skeleton, writing takes 5 minutes per script",
          ]},
        ],
      },
      {
        id: "s6",
        title: "Use Auto Clips for long-form → short-form",
        body: [
          { type: "p", text: "If you already have a podcast, vlog, or livestream, you don't need to film anything else. Feed the long video into Auto Clips and Nexus + Claude will hand you 6 draft short-form clips in 2 minutes." },
          { type: "action", label: "Try it now", cta: "Open Auto Clips", href: "/creator-studio?tab=auto-clips" },
        ],
      },
      {
        id: "s7",
        title: "Never edit and film on the same day",
        body: [
          { type: "p", text: "Filming needs high energy. Editing needs low energy + attention to detail. Doing both in one day drains you and both stages suffer." },
          { type: "list", items: [
            "Film day: high energy, morning, batch multiple looks",
            "Edit day: any time, low energy is fine, wear pyjamas",
            "You need TWO 90-min sprints per week, not one 3-hour session",
          ]},
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ RETAINERS
  {
    id: "m6",
    title: "Turning brand deals into recurring retainers",
    tag: "Business",
    tone: "text-nexus-pink",
    accent: "from-nexus-pink to-nexus-purple",
    duration: 11,
    icon: "TrendingUp",
    summary: "How to turn a one-off $500 collab into a $3,000/month retainer — the mindset shift and the exact pitch to use.",
    steps: [
      {
        id: "s1",
        title: "Why one-off deals are the wrong game",
        body: [
          { type: "p", text: "Every one-off collab is 100% new business every month. That means every month you're back to zero, hunting for the next campaign, competing on price." },
          { type: "p", text: "Retainers flip this. One signed retainer = 3-12 months of predictable income and time freed up to make better content instead of hustling for the next gig." },
          { type: "quote", text: "One retainer client is worth ten one-off collabs — not just financially, mentally.", cite: "Every 6-figure Nexus creator" },
        ],
      },
      {
        id: "s2",
        title: "Only pitch retainers after you've delivered one great deal",
        body: [
          { type: "warn", label: "Don't do this", text: "Never pitch a retainer in the first email. Deliver ONE excellent campaign first — over-deliver on the metrics, be a joy to work with, then pitch." },
          { type: "list", items: [
            "Post 24h before the deadline",
            "Send a screenshot of results within 48h of posting",
            "Ask if they want to see the raw analytics — most brands don't get this",
            "Only THEN pitch the retainer",
          ]},
        ],
      },
      {
        id: "s3",
        title: "The 3-post retainer pitch",
        body: [
          { type: "h", text: "The exact email/message to send" },
          { type: "quote", text: "\"The launch post pulled 12k views + 340 saves — 2.8× my average. If it's useful, I could do 3 posts per month for you on retainer at [rate]. Cheaper per-post than the one-off, plus you skip the negotiation cycle every time. Want me to send terms?\"" },
          { type: "list", items: [
            "Lead with the metric that beat their expectations",
            "Frame the retainer as saving THEM time and money",
            "Give them a one-word decision: \"Want terms?\" — not a five-question email",
          ]},
        ],
      },
      {
        id: "s4",
        title: "How to price a retainer",
        body: [
          { type: "p", text: "Retainers should be 15-25% CHEAPER per-post than a one-off. That's the trade — you get certainty and less admin, they get a discount." },
          { type: "h", text: "Example" },
          { type: "list", items: [
            "One-off Reel rate: $500",
            "Retainer of 3 posts/month: 3 × $500 × 0.8 = $1,200/mo",
            "Retainer of 4 posts/month: 4 × $500 × 0.75 = $1,500/mo",
            "12-month retainer: add another 5-10% discount for the certainty",
          ]},
        ],
      },
      {
        id: "s5",
        title: "The 3 clauses your retainer MUST have",
        body: [
          { type: "list", items: [
            "1. Monthly invoicing (paid in advance) — not on completion",
            "2. 30-day termination clause on either side — no lock-ins that scare brands off",
            "3. Cap on revisions per post (2 rounds max) — protects your sanity",
          ]},
          { type: "tip", label: "Nexus tip", text: "Set up a recurring campaign on Nexus — every month the escrow auto-funds and you get paid on delivery, no re-quoting." },
        ],
      },
      {
        id: "s6",
        title: "Diversify across 2-3 retainers, never more",
        body: [
          { type: "p", text: "Two retainers = safety. Three retainers = predictable income. Four+ retainers = you're a content agency and quality drops." },
          { type: "p", text: "The magic number is 2-3 retainer clients + 1-2 spot deals per month for variety." },
        ],
      },
      {
        id: "s7",
        title: "How to renew a retainer without asking",
        body: [
          { type: "p", text: "Six weeks before the retainer ends, send this: a Loom video walking through the last 90 days of results + your ideas for the next 90 days." },
          { type: "list", items: [
            "Show growth in the brand's follower/engagement/click metrics",
            "Propose 1-2 fresh content angles you haven't tried",
            "Suggest one small rate increase (5-10%) — anchored to your growth since signing",
          ]},
        ],
      },
      {
        id: "s8",
        title: "When to walk away from a retainer",
        body: [
          { type: "warn", label: "Red flags after signing", text: "If any of these show up 60 days in, don't renew:" },
          { type: "list", items: [
            "Endless revision requests beyond what was agreed",
            "Late payments beyond Net 30 more than once",
            "New \"just one more thing\" scope creep every week",
            "Brand contact becomes uncontactable / silent",
          ]},
          { type: "p", text: "A bad retainer will kill your creative output more than a slow month. Trust your gut and walk when it turns." },
        ],
      },
    ],
  },
];

export function getModule(id) {
  return MODULES.find((m) => m.id === id) || null;
}

/** localStorage key namespacing — kept as an offline cache + fallback for logged-out users. */
export function progressKey(moduleId) {
  return `nexus_academy_progress_${moduleId}`;
}

/** Read local cache. Returns { completed: string[], updatedAt: iso | null } */
export function readProgress(moduleId) {
  try {
    const raw = localStorage.getItem(progressKey(moduleId));
    if (!raw) return { completed: [], updatedAt: null };
    const parsed = JSON.parse(raw);
    return { completed: Array.isArray(parsed.completed) ? parsed.completed : [], updatedAt: parsed.updatedAt || null };
  } catch {
    return { completed: [], updatedAt: null };
  }
}

/** Local-only cache write (kept in sync by AcademyModule after every server call). */
export function writeProgress(moduleId, completed) {
  try {
    localStorage.setItem(progressKey(moduleId), JSON.stringify({
      completed,
      updatedAt: new Date().toISOString(),
    }));
  } catch { /* ignore */ }
}

export function resetProgress(moduleId) {
  try { localStorage.removeItem(progressKey(moduleId)); } catch { /* ignore */ }
}

// ---------- Server sync helpers ----------
/**
 * Hydrate localStorage from the /api/academy/progress endpoint so this device
 * mirrors the creator's most recent progress from any other device.
 * Silent if unauthed / offline — localStorage remains the fallback.
 */
export async function pullServerProgress(api) {
  try {
    const r = await api.get("/academy/progress");
    const rows = r?.data?.progress || [];
    for (const row of rows) {
      writeProgress(row.module_id, row.completed || []);
    }
    return rows;
  } catch {
    return null;
  }
}

/**
 * Push the current completed list for a module to the server.
 * Returns the server payload ({ progress, is_complete, just_awarded }) or null.
 */
export async function pushServerProgress(api, module, completed) {
  if (!module) return null;
  try {
    const r = await api.post("/academy/progress", {
      module_id: module.id,
      module_title: module.title,
      module_tag: module.tag || "",
      module_accent: module.accent || "",
      module_icon: module.icon || "",
      completed: completed || [],
      total_steps: module.steps.length,
    });
    return r?.data || null;
  } catch {
    return null;
  }
}

/** Wipe both local + server for a single module. */
export async function resetServerProgress(api, moduleId) {
  resetProgress(moduleId);
  try { await api.delete(`/academy/progress/${moduleId}`); } catch { /* ignore */ }
}
